import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useLocationHandling } from '../LocationHandling/useLocationHandling';
import { useTranslation } from 'react-i18next';

type Prefs = {
  analyticsConsent: boolean;
  pushConsent: boolean;
  locationConsent: boolean;
};

const defaultPrefs: Prefs = {
  analyticsConsent: false,
  pushConsent: false,
  locationConsent: false,
};

const ToggleRow: React.FC<{
  title: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  yesLabel: string;
  noLabel: string;
}> = ({ title, description, checked, onChange, yesLabel, noLabel }) => {
  return (
    <div style={row}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {description ? <div style={desc}>{description}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        style={{
          ...toggleBtn,
          background: checked ? '#10B981' : '#EF4444', // green / red
        }}
      >
        {checked ? yesLabel : noLabel}
      </button>
    </div>
  );
};

const RequirePrivacyChangeCL: React.FC = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const returnTo = sp.get('return') || '/gallery';
  const { t } = useTranslation();

  const [prefs, setPrefs] = useState<Prefs>(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { requestMyLocation } = useLocationHandling();

  // 🔝 Portal mount node
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = document.createElement('div');
    el.setAttribute('data-overlay-root', 'require-privacy-change-cl');
    document.body.appendChild(el);
    setPortalEl(el);

    // lock background scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prev;
      document.body.removeChild(el);
    };
  }, []);

  // Load current preferences (fallback to defaults)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/privacy/preferences');
        setPrefs({ ...defaultPrefs, ...data });
      } catch {
        setPrefs(defaultPrefs);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setKey =
    (key: keyof Prefs) =>
    (v: boolean) =>
      setPrefs((p) => ({ ...p, [key]: v }));

  const handleContinue = async () => {
    try {
      setSaving(true);
      setError('');

      // 1) save prefs server-side (axios adds Authorization + CSRF header)
      await api.put('/privacy/preferences', { ...prefs });

      // 2) keep local cache in sync (helps routing decisions elsewhere)
      localStorage.setItem(
        'privacy:locationConsent',
        String(!!prefs.locationConsent)
      );

      // 3) if the user enabled location here, fetch right away
      if (prefs.locationConsent) {
        try {
          await requestMyLocation();
        } catch (e) {
          console.warn('Auto-geolocation after consent failed:', e);
        }
      }

      // 4) go back to where we came from
      navigate(returnTo, { replace: true });
    } catch (e: any) {
      setError(
        e?.response?.data?.msg || t('requirePrivacyChangeCL.errorSave')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate(returnTo);
  };

  if (!portalEl) return null;

  return (
    <div style={overlayWrap}>
      <div style={panel} role="dialog" aria-modal="true" aria-labelledby="rpc-title">
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('requirePrivacyChangeCL.close')}
            style={closeBtn}
          >
            ✕ {t('requirePrivacyChangeCL.close')}
          </button>
        </div>

        <h2 id="rpc-title" style={title}>
          {t('requirePrivacyChangeCL.title')}
        </h2>

        <div style={card}>
          {loading ? (
            <p>{t('firstRunPrivacy.loading')}</p>
          ) : (
            <>
              <ToggleRow
                title={t('firstRunPrivacy.toggles.locationTitle')}
                description={t('firstRunPrivacy.toggles.locationDesc')}
                checked={prefs.locationConsent}
                onChange={setKey('locationConsent')}
                yesLabel={t('firstRunPrivacy.yes')}
                noLabel={t('firstRunPrivacy.no')}
              />
              <ToggleRow
                title={t('firstRunPrivacy.toggles.analyticsTitle')}
                description={t('firstRunPrivacy.toggles.analyticsDesc')}
                checked={prefs.analyticsConsent}
                onChange={setKey('analyticsConsent')}
                yesLabel={t('firstRunPrivacy.yes')}
                noLabel={t('firstRunPrivacy.no')}
              />
              <ToggleRow
                title={t('firstRunPrivacy.toggles.pushTitle')}
                description={t('firstRunPrivacy.toggles.pushDesc')}
                checked={prefs.pushConsent}
                onChange={setKey('pushConsent')}
                yesLabel={t('firstRunPrivacy.yes')}
                noLabel={t('firstRunPrivacy.no')}
              />

              {error && <div style={errBox}>{error}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={saving}
                  style={continueBtn}
                >
                  {saving
                    ? t('firstRunPrivacy.saving')
                    : t('firstRunPrivacy.continue')}
                </button>
              </div>

              <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                {t('firstRunPrivacy.footerNote')}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ——— styles ——— */
const overlayWrap: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '60px 16px',
  zIndex: 9999,
};

const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 760,
  color: '#111',
  background: 'transparent',
};

const title: React.CSSProperties = {
  fontSize: 24,
  margin: '0 0 14px 0',
  color: '#fff',
};

const card: React.CSSProperties = {
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 12,
  padding: 16,
  background: 'rgba(255,255,255,0.98)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
};

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  borderBottom: '1px solid #f1f5f9',
};

const desc: React.CSSProperties = { color: '#6b7280', fontSize: 13, marginTop: 4 };

const toggleBtn: React.CSSProperties = {
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '8px 14px',
  cursor: 'pointer',
  minWidth: 70,
  fontWeight: 600,
};

const continueBtn: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  color: '#fff',
  background: 'rgb(56, 6, 117)',
  cursor: 'pointer',
  minWidth: 140,
};

const errBox: React.CSSProperties = {
  background: '#fff7ed',
  border: '1px solid #fdba74',
  color: '#7c2d12',
  padding: 10,
  borderRadius: 8,
  marginTop: 10,
};

const closeBtn: React.CSSProperties = {
  position: 'absolute',
  right: 0,
  top: -42,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
  color: '#e5e7eb',
};

export default RequirePrivacyChangeCL;
